import React, { useEffect, useMemo, useState, useRef } from "react";
import {
	fetchPlanning,
	fetchDepartments,
	fetchAppointmentLogs,
	cancelHolidayRequest,
	deleteAppointmentLog,
} from "./api";
import { toast } from "react-toastify";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin from "@fullcalendar/interaction";
import "react-toastify/dist/ReactToastify.css";
import { format } from "date-fns";

// --- helpers dates ---
// --- helpers dates (timezone-safe, sans UTC) ---
const isValidDate = (d) => d instanceof Date && !isNaN(d.getTime());

// Parse toujours en date locale (pas en UTC)
const toLocalDate = (v) => {
	if (v instanceof Date) {
		return new Date(v.getFullYear(), v.getMonth(), v.getDate());
	}
	if (typeof v === "string") {
		// Supporte "YYYY-MM-DD" (ou ISO): on lit juste la partie date
		const m = v.match(/^(\d{4})-(\d{2})-(\d{2})/);
		if (m) {
			const y = parseInt(m[1], 10);
			const mo = parseInt(m[2], 10) - 1;
			const d = parseInt(m[3], 10);
			return new Date(y, mo, d);
		}
		const d = new Date(v);
		return new Date(d.getFullYear(), d.getMonth(), d.getDate());
	}
	const d = new Date(v);
	return new Date(d.getFullYear(), d.getMonth(), d.getDate());
};

// Retourne "YYYY-MM-DD" en local
const ymd = (input) => {
	if (!input) return "";
	const d = toLocalDate(input);
	if (!isValidDate(d)) return "";
	const y = d.getFullYear();
	const m = String(d.getMonth() + 1).padStart(2, "0");
	const day = String(d.getDate()).padStart(2, "0");
	return `${y}-${m}-${day}`;
};

const addDays = (value, n) => {
	const d = toLocalDate(value);
	if (!isValidDate(d)) return null;
	d.setDate(d.getDate() + n);
	return d;
};

const inclusiveToExclusiveEnd = (endInclusive) => {
	const d = addDays(endInclusive, 1);
	return d ? ymd(d) : "";
};

const exclusiveToInclusiveEnd = (endExclusive) => {
	const d = addDays(endExclusive, -1);
	return d ? ymd(d) : "";
};

// Safe formatter pour table/modal
const fmtYMD = (value) => {
	const d = toLocalDate(value);
	return isValidDate(d) ? format(d, "dd/MM/yyyy") : "-";
};



// Estimation heures: Lun–Jeu = 10h, half = 5h (sans jours fériés)
const estimateHours = (startISO, endISO, startType, endType) => {
	const start = new Date(ymd(startISO));
	const end = new Date(ymd(endISO));

	if (start.getTime() === end.getTime()) {
		return startType === "morning" || startType === "afternoon" ? 5 : 10;
	}
	let total = 0;
	for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
		const dow = d.getDay(); // 1..4 = Lun..Jeu
		if (dow >= 1 && dow <= 4) {
			const isStart = d.toDateString() === start.toDateString();
			const isEnd = d.toDateString() === end.toDateString();
			if (isStart)
				total += startType === "morning" || startType === "afternoon" ? 5 : 10;
			else if (isEnd)
				total += endType === "morning" || endType === "afternoon" ? 5 : 10;
			else total += 10;
		}
	}
	return total;
};

function Planning() {
	const [holidays, setHolidays] = useState([]);
	const [appointmentLogs, setAppointmentLogs] = useState([]);
	const [departments, setDepartments] = useState([]);
	const [selectedDepartment, setSelectedDepartment] = useState("");

	const [dateRange, setDateRange] = useState({ start: "", end: "" }); // YYYY-MM-DD
	const [showListView, setShowListView] = useState(false);
	const [searchTerm, setSearchTerm] = useState("");

	// liste: "month" (YYYY-MM) ou "year" (YYYY)
	const [viewMode, setViewMode] = useState("month");
	const [selectedMonth, setSelectedMonth] = useState(
		new Date().toISOString().slice(0, 7)
	);
	const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

	const [selectedEventType, setSelectedEventType] = useState("all"); // all | holiday | appointment
	const [selectedEvent, setSelectedEvent] = useState(null);

	// Year selector for calendar view
	const [calendarYear, setCalendarYear] = useState(new Date().getFullYear());
	const calendarRef = useRef(null);

	const goToCalendarYear = (yr) => {
		const y = parseInt(yr, 10);
		if (!y) return;
		const api = calendarRef.current?.getApi?.();
		if (api) {
			// on va au 1er janvier de l'année choisie
			api.gotoDate(new Date(y, 0, 1));
		}
	};

	const departmentColors = {
		Production: "#A7F3D0",
		Cleaning: "#D1D5DB",
		Directors: "#81E6D9",
		Engineers: "#FDE68A",
		Floor: "#FB923C",
		G3: "#67E8F9",
		Office: "#F87171",
		Process: "#FBCFE8",
		QC: "#1D4ED8",
		Warehouse: "#C4B5FD",
		Appointment: "#a0aec0",
	};

	// --- load departments once ---
	useEffect(() => {
		(async () => {
			try {
				const resp = await fetchDepartments();
				setDepartments(resp.data);
			} catch {
				toast.error("Failed to fetch departments");
			}
		})();
	}, []);

	// --- fetch holidays for current range/department ---
	const fetchHolidaysData = async (startStr, endStr, department) => {
		try {
			const { data } = await fetchPlanning(startStr, endStr, department);
			setHolidays(data);
		} catch {
			toast.error("Failed to fetch holiday planning");
		}
	};

	useEffect(() => {
		if (dateRange.start && dateRange.end) {
			fetchHolidaysData(dateRange.start, dateRange.end, selectedDepartment);
		}
	}, [dateRange, selectedDepartment]);

	// --- appointments once (you can refetch after delete) ---
	useEffect(() => {
		(async () => {
			try {
				const { data } = await fetchAppointmentLogs();
				setAppointmentLogs(data);
			} catch {
				toast.error("Failed to fetch appointment logs");
			}
		})();
	}, []);

	// FullCalendar date window
	const handleDatesChange = ({ startStr, endStr }) => {
		const start = ymd(startStr);
		const endInclusive = ymd(addDays(endStr, -1));
		setDateRange({ start, end: endInclusive });
		if (start && endInclusive) {
			fetchHolidaysData(start, endInclusive, selectedDepartment);
		}
	};


	// List mode: react to month/year/department
	useEffect(() => {
		if (!showListView) return; // calendar mode drives its own range
		if (viewMode === "month" && selectedMonth) {
			const [y, m] = selectedMonth.split("-").map(Number);
			const start = ymd(new Date(y, m - 1, 1));
			const end = ymd(new Date(y, m, 0));
			setDateRange({ start, end });
			fetchHolidaysData(start, end, selectedDepartment);
		} else if (viewMode === "year" && selectedYear) {
			const start = `${selectedYear}-01-01`;
			const end = `${selectedYear}-12-31`;
			setDateRange({ start, end });
			fetchHolidaysData(start, end, selectedDepartment);
		}
	}, [showListView, viewMode, selectedMonth, selectedYear, selectedDepartment]);

	// --- build events ---

	// Holidays -> all-day, end exclusive
	const holidayEvents = useMemo(() => {
		return holidays
			.map((h) => {
			const startYMD = ymd(h.start_date);
			const endYMD   = ymd(h.end_date);

			if (!startYMD || !endYMD) return null; // skip invalid

			const hours =
				  typeof h.hours === "number"
			? h.hours
			: estimateHours(startYMD, endYMD, h.start_day_type, h.end_day_type);
			const startText =
				  h.start_day_type === "morning"  ? "Morning"  :
			h.start_day_type === "afternoon"? "Afternoon": "Full Day";
			const endText =
				  h.end_day_type === "morning"    ? "Morning"  :
			h.end_day_type === "afternoon"  ? "Afternoon": "Full Day";

			const color = departmentColors[h.department] || "#D1D5DB";
			const endExclusive = inclusiveToExclusiveEnd(endYMD);
			if (!endExclusive) return null; // still invalid? skip

			return {
				id: String(h.id),
				title: `${h.firstName} ${h.lastName} (${startText} - ${endText}, ${hours}h)`,
				start: startYMD,
				end: endExclusive, // exclusive
				backgroundColor: color,
				borderColor: color,
				allDay: true,
				extendedProps: {
					type: "holiday",
					holidayId: String(h.id),
					department: h.department,
					start_day_type: h.start_day_type,
					end_day_type: h.end_day_type,
					hours,
					status: h.status,
					user_id: h.user_id,
				},
			};
		})
			.filter(Boolean); // remove nulls
	}, [holidays]);


	// Appointments -> all-day, end exclusive
	const appointmentEvents = useMemo(() => {
		return appointmentLogs
			.map((log) => {
			const startYMD = ymd(log.event_from_date);
			const toYMD    = ymd(log.event_to_date || log.event_from_date);
			if (!startYMD || !toYMD) return null; // skip invalid

			const endExclusive = inclusiveToExclusiveEnd(toYMD);
			if (!endExclusive) return null;

			const color = departmentColors.Appointment;
			return {
				id: String(log.id),
				title: `Appointment: ${log.firstName} ${log.lastName} - ${log.reason}`,
				start: startYMD,
				end: endExclusive, // exclusive
				backgroundColor: color,
				borderColor: color,
				allDay: true,
				extendedProps: {
					type: "appointment",
					department: "Appointment",
					start_time: log.start_time,
					end_time: log.end_time,
				},
			};
		})
			.filter(Boolean);
	}, [appointmentLogs]);


	const combinedEvents = useMemo(
		() => [...holidayEvents, ...appointmentEvents],
		[holidayEvents, appointmentEvents]
	);

	// Type filter
	const filteredEvents = useMemo(() => {
		if (selectedEventType === "all") return combinedEvents;
		return combinedEvents.filter(
			(e) => e.extendedProps.type === selectedEventType
		);
	}, [combinedEvents, selectedEventType]);

	// Text filter
	const finalEvents = useMemo(() => {
		const q = searchTerm.trim().toLowerCase();
		if (!q) return filteredEvents;
		return filteredEvents.filter((e) => {
			const title = (e.title || "").toLowerCase();
			const dept = (e.extendedProps.department || "").toLowerCase();
			return title.includes(q) || dept.includes(q);
		});
	}, [filteredEvents, searchTerm]);

	// List view range overlap
	const listEvents = useMemo(() => {
		if (!showListView) return finalEvents;
		if (!dateRange.start || !dateRange.end) return finalEvents;

		const viewStart = new Date(dateRange.start);
		const viewEnd = new Date(dateRange.end);

		return finalEvents.filter((e) => {
			const s = new Date(ymd(e.start));
			const eExclusive = new Date(ymd(e.end));
			const eInclusive = addDays(eExclusive, -1);
			// overlap if s <= viewEnd && eInclusive >= viewStart
			return s <= viewEnd && eInclusive >= viewStart;
		});
	}, [finalEvents, showListView, dateRange]);

	const sortedEvents = useMemo(
		() => [...listEvents].sort((a, b) => new Date(a.start) - new Date(b.start)),
		[listEvents]
	);

	// Click
	const handleEventClick = (info) => setSelectedEvent(info.event);

	// Delete / cancel
	const handleDeleteEvent = async () => {
		if (!selectedEvent) return;
		const type = selectedEvent.extendedProps.type;

		if (type === "holiday" && selectedEvent.extendedProps.holidayId) {
			if (!window.confirm("Do you want to cancel this holiday request?"))
				return;
			try {
				await cancelHolidayRequest(selectedEvent.extendedProps.holidayId);
				toast.success("Holiday cancelled successfully.");
				// refetch holidays & appointments
				if (dateRange.start && dateRange.end) {
					await fetchHolidaysData(
						dateRange.start,
						dateRange.end,
						selectedDepartment
					);
				}
				const appResp = await fetchAppointmentLogs();
				setAppointmentLogs(appResp.data);
				setSelectedEvent(null);
			} catch {
				toast.error("Failed to submit cancellation request");
			}
			return;
		}

		if (type === "appointment") {
			if (!window.confirm("Do you want to delete this appointment?")) return;
			try {
				await deleteAppointmentLog(selectedEvent.id);
				toast.success("Appointment deleted successfully.");
				const appResp = await fetchAppointmentLogs();
				setAppointmentLogs(appResp.data);
				setSelectedEvent(null);
			} catch {
				toast.error("Failed to delete appointment");
			}
		}
	};

	return (
		<div className="container mx-auto p-6">
		<h1 className="text-3xl font-semibold mb-6 text-center">
		Holiday Planning
		</h1>

		<div className="flex justify-between items-center mb-6">
		<button
		onClick={() => setShowListView((v) => !v)}
className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
>
	{showListView ? "View Calendar" : "View List"}
</button>

{/* Calendar year picker (visible seulement en vue calendrier) */}
{!showListView && (
	<div className="flex items-end gap-3">
	<div>
	<label className="block text-gray-700 text-sm font-bold mb-2">
	Calendar Year
	</label>
	<input
	type="number"
	min={2000}
max={2100}
value={calendarYear}
onChange={(e) => setCalendarYear(parseInt(e.target.value || 0, 10))}
className="border rounded px-3 py-2 w-28"
/>
	</div>
<button
type="button"
onClick={() => goToCalendarYear(calendarYear)}
className="h-10 px-4 rounded bg-gray-300 hover:bg-gray-400"
>
	Go
</button>
</div>
)}

	<div>
		<label className="block text-gray-700 text-sm font-bold mb-2">
			Filter by Department
				</label>
<select
value={selectedDepartment}
onChange={(e) => {
	setSelectedDepartment(e.target.value);
	if (dateRange.start && dateRange.end) {
		fetchHolidaysData(
			dateRange.start,
			dateRange.end,
			e.target.value
		);
	}
}}
className="border rounded px-4 py-2 w-full"
>
	<option value="">All Departments</option>
{departments.map((d) => (
	<option key={d.id} value={d.name}>
	{d.name}
</option>
))}
	</select>
</div>

{showListView && (
	<div className="flex flex-col space-y-2">
	<div>
	<label className="block text-gray-700 text-sm font-bold mb-1">
	Event Type
	</label>
	<select
	value={selectedEventType}
onChange={(e) => setSelectedEventType(e.target.value)}
className="border rounded px-4 py-2 w-full"
>
	<option value="all">All Events</option>
<option value="holiday">Holidays</option>
<option value="appointment">Appointments</option>
</select>
</div>
<div className="flex items-center space-x-4">
	<button
onClick={() => setViewMode("month")}
className={`px-4 py-2 rounded ${
		   viewMode === "month"
		   ? "bg-blue-500 text-white"
		   : "bg-gray-300 text-gray-700"
		  }`}
              >
                View by Month
              </button>
              <button
                onClick={() => setViewMode("year")}
                className={`px-4 py-2 rounded ${
					viewMode === "year"
						? "bg-blue-500 text-white"
					: "bg-gray-300 text-gray-700"
				}`}
              >
                View by Year
              </button>
            </div>
            {viewMode === "month" && (
  <div>
    <label className="block text-gray-700 text-sm font-bold mb-2">
      Select a Month
    </label>

    {(() => {
      // On découpe selectedMonth "YYYY-MM" -> year/month
      const [yyStr, mmStr] = String(selectedMonth || "").split("-");
      const ymYear = Number(yyStr) || new Date().getFullYear();
      const ymMonth = Number(mmStr) || (new Date().getMonth() + 1);

      const setYear = (y) => {
        const clamped = Math.max(2000, Math.min(2100, Number(y) || 0));
        const mm = String(ymMonth).padStart(2, "0");
        setSelectedMonth(`${clamped}-${mm}`);
      };

      const setMonth = (m) => {
        const mm = String(Number(m) || 1).padStart(2, "0");
        setSelectedMonth(`${ymYear}-${mm}`);
      };

      return (
        <div className="flex items-end gap-3">
          {/* Année */}
          <div>
            <label className="block text-gray-700 text-xs font-semibold mb-1">
              Year
            </label>
            <input
              type="number"
              value={ymYear}
              min={2000}
              max={2100}
              onChange={(e) => setYear(e.target.value)}
              className="border rounded px-3 py-2 w-28"
            />
          </div>

          {/* Mois */}
          <div>
            <label className="block text-gray-700 text-xs font-semibold mb-1">
              Month
            </label>
            <select
              value={ymMonth}
              onChange={(e) => setMonth(e.target.value)}
              className="border rounded px-3 py-2"
            >
              <option value={1}>January</option>
              <option value={2}>February</option>
              <option value={3}>March</option>
              <option value={4}>April</option>
              <option value={5}>May</option>
              <option value={6}>June</option>
              <option value={7}>July</option>
              <option value={8}>August</option>
              <option value={9}>September</option>
              <option value={10}>October</option>
              <option value={11}>November</option>
              <option value={12}>December</option>
            </select>
          </div>

          {/* Raccourcis mois -/+ (franchissent le changement d'année) */}
          <div className="flex items-center gap-2">
            <button
              type="button"
              className="px-3 py-2 rounded bg-gray-300 hover:bg-gray-400"
              title="Previous month"
              onClick={() => {
                const d = new Date(ymYear, ymMonth - 1, 1);
                d.setMonth(d.getMonth() - 1);
                const y = d.getFullYear();
                const m = String(d.getMonth() + 1).padStart(2, "0");
                setSelectedMonth(`${y}-${m}`);
              }}
            >
              ‹
            </button>
            <button
              type="button"
              className="px-3 py-2 rounded bg-gray-300 hover:bg-gray-400"
              title="Next month"
              onClick={() => {
                const d = new Date(ymYear, ymMonth - 1, 1);
                d.setMonth(d.getMonth() + 1);
                const y = d.getFullYear();
                const m = String(d.getMonth() + 1).padStart(2, "0");
                setSelectedMonth(`${y}-${m}`);
              }}
            >
              ›
            </button>
          </div>
        </div>
      );
    })()}
  </div>
)}

            {viewMode === "year" && (
              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2">
                  Select a Year
                </label>
                <input
                  type="number"
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(Number(e.target.value))}
                  className="border rounded px-4 py-2"
                />
              </div>
            )}
          </div>
        )}
      </div>

      {showListView && (
        <div className="mb-4">
          <input
            type="text"
            placeholder="Search by name, surname, or department"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg shadow-sm"
          />
        </div>
      )}

      {!showListView ? (
        <FullCalendar
		ref={calendarRef}
          plugins={[dayGridPlugin, interactionPlugin]}
          initialView="dayGridMonth"
          firstDay={1}
          events={finalEvents}
          headerToolbar={{
            left: "prev,next today",
            center: "title",
            right: "dayGridYear,dayGridMonth,dayGridWeek",
          }}
          height="auto"
          displayEventTime={false}
datesSet={({ startStr, endStr, view }) => {
  // keep your fetch range logic
  handleDatesChange({ startStr, endStr });

  // use the logical start of the current view (month/week), not the grid's first cell
  const y = view?.currentStart
    ? view.currentStart.getFullYear()
    : new Date(startStr).getFullYear(); // fallback

  setCalendarYear(y);
}}
          dayMaxEvents={4}
          eventClick={handleEventClick}
        />
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full bg-white border border-gray-300 rounded-lg shadow-md">
            <thead className="bg-gold">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-700 uppercase">
                  Name
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-700 uppercase">
                  Department
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-700 uppercase">
                  Start Date
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-700 uppercase">
                  End Date
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-700 uppercase">
                  Type
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-700 uppercase">
                  Hours
                </th>
              </tr>
            </thead>
            <tbody>
              {sortedEvents.map((event) => {
                const startHuman = format(
                  new Date(ymd(event.start)),
                  "dd/MM/yyyy"
                );
                const endHuman = format(
                  new Date(exclusiveToInclusiveEnd(event.end)),
                  "dd/MM/yyyy"
                );
                const bg =
                  event.extendedProps.department &&
                  departmentColors[event.extendedProps.department]
                    ? departmentColors[event.extendedProps.department]
                    : "#FFF";
                return (
                  <tr
                    key={event.id || event.start}
                    className="hover:bg-gray-100"
                    style={{ backgroundColor: bg }}
                  >
                    <td className="px-4 py-2 border">{event.title}</td>
                    <td className="px-4 py-2 border">
                      {event.extendedProps.department}
                    </td>
                    <td className="px-4 py-2 border">{startHuman}</td>
                    <td className="px-4 py-2 border">{endHuman}</td>
                    <td className="px-4 py-2 border">
                      {event.extendedProps.type}
                    </td>
                    <td className="px-4 py-2 border">
                      {event.extendedProps.type === "holiday"
                        ? `${event.extendedProps.hours} hours`
                        : "N/A"}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal */}
      {selectedEvent && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-md">
            <h2 className="text-2xl font-bold mb-4">
              {selectedEvent.extendedProps.type === "holiday"
                ? "Holiday Request Details"
                : "Appointment Details"}
            </h2>
            <div className="mb-4">
              <p>
                <strong>Name:</strong> {selectedEvent.title}
              </p>
              <p>
                <strong>Department:</strong>{" "}
                {selectedEvent.extendedProps.department}
              </p>
              <p>
                <strong>Start Date:</strong>{" "}
                {format(new Date(ymd(selectedEvent.start)), "dd/MM/yyyy")}
              </p>
              <p>
                <strong>End Date:</strong>{" "}
                {format(
                  new Date(exclusiveToInclusiveEnd(selectedEvent.end)),
                  "dd/MM/yyyy"
                )}
              </p>

              {selectedEvent.extendedProps.type === "holiday" && (
                <>
                  <p>
                    <strong>Day Type:</strong>{" "}
                    {selectedEvent.extendedProps.start_day_type} to{" "}
                    {selectedEvent.extendedProps.end_day_type}
                  </p>
                  <p>
                    <strong>Hours:</strong> {selectedEvent.extendedProps.hours}
                  </p>
                  <p>
                    <strong>Status:</strong>{" "}
                    {selectedEvent.extendedProps.status}
                  </p>
                </>
              )}

              {selectedEvent.extendedProps.type === "appointment" && (
                <>
                  <p>
                    <strong>Start Time:</strong>{" "}
                    {selectedEvent.extendedProps.start_time}
                  </p>
                  <p>
                    <strong>End Time:</strong>{" "}
                    {selectedEvent.extendedProps.end_time}
                  </p>
                  <p>
                    <strong>Reason:</strong> {selectedEvent.title}
                  </p>
                </>
              )}
            </div>

            <div className="flex justify-end space-x-4">
              <button
                onClick={() => setSelectedEvent(null)}
                className="bg-gray-500 hover:bg-gray-700 text-white font-bold py-2 px-4 rounded"
              >
                Close
              </button>
              <button
                onClick={handleDeleteEvent}
                className="bg-red-500 hover:bg-red-700 text-white font-bold py-2 px-4 rounded"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Planning;
